import filterText from './filter-text';

export interface breakModel {
	phrase: string;
	subset: string[];
	bold: boolean;
	italic: boolean;
	maxPxLength: number;
	maxPxHeight: number;
	fontface: string;
	pointsize: number;
	resize: {
		up: number;
		down: number;
	};
}

export interface testModel {
	pointsize: number;
	valid: boolean;
	lines: string[];
	linesWithHyphen: string[];
}

export interface BreakTextResult {
	pointsize: number;
	phraseArray: string[];
	textheight: number;
	text_formatted: string;
	text_formatted_for_canvas: string;
}

/**
 * Breaks a long word into smaller parts that fit within `maxPxLength`, measured using a canvas context
 * @param word The word to break
 * @param canvasContext The 2D canvas context for measuring text width
 * @param props The `breakModel` properties, including `maxPxLength`
 * @returns An array of word parts, each fitting within `maxPxLength`
 */
function breakWord(
	word: string,
	canvasContext: CanvasRenderingContext2D,
	props: breakModel,
): string[] {
	const parts: string[] = [];
	let remaining = word;

	while (remaining.length > 0) {
		let wordLength = 1;

		/**
		 * Measure characters until exceeding `maxPxLength`
		 */
		while (
			wordLength <= remaining.length
			&& canvasContext.measureText(
				remaining.substring(
					0,
					wordLength,
				),
			).width <= props.maxPxLength
		) {
			wordLength += 1;
		}

		/**
		 * Backtrack to last fitting length
		 */
		wordLength -= 1;

		/**
		 * Handle edge case: single character too wide
		 */
		if (wordLength === 0) {
			parts.push(
				remaining.substring(
					0,
					1,
				),
			);
			remaining = remaining.substring(1);
		} else {
			parts.push(
				remaining.substring(
					0,
					wordLength,
				),
			);
			remaining = remaining.substring(wordLength);
		}
	}

	return parts;
}

/**
 * Selects the best-fitting `pointsize` from tested results, prioritizing the largest valid size
 * @param tested Array of `testModel` objects containing `pointsize` results
 * @returns The best-fitting `testModel`, or the smallest tested size if no valid fit exists
 */
function getBestFit(tested: testModel[]): testModel | undefined {
	const valids = tested.filter((obj) => (
		obj.valid === true
	));

	if (valids.length) {
		const sortedValidSizes = valids.sort((a, b) => (
			a.pointsize - b.pointsize
		));
		const objLargest = sortedValidSizes[sortedValidSizes.length - 1];

		return tested.find((obj) => (
			obj.pointsize === objLargest?.pointsize
		));
	}

	const sortedSizes = tested.sort((a, b) => (
		a.pointsize - b.pointsize
	));
	const objSmallest = sortedSizes[0];

	return tested.find((obj) => (
		obj.pointsize === objSmallest?.pointsize
	));
}

/**
 * Tests a specific `pointsize` by breaking text into lines and checking if it fits within constraints.
 * Uses canvas-based measurements for efficiency.
 * @param pointsize The font size to test, in points
 * @param canvasContext The 2D canvas context for measuring text
 * @param lines The input text split by hard line breaks
 * @param props The `breakModel` properties defining constraints and styling
 * @returns A `testModel` object with the results of the `pointsize` test
 */
function testPointsize(
	pointsize: number,
	canvasContext: CanvasRenderingContext2D,
	lines: string[],
	props: breakModel,
): testModel {
	const arrLines: string[] = [];
	const arrLinesWithHyphen: string[] = [];
	/**
	 * Convert pointsize to pixels
	 */
	const pxsize = pointsize * 31496 / 15120;

	if (
		props.italic
		&& props.bold
	) {
		canvasContext.font = `${pxsize}px ${props.fontface} BoldItalic`;
	} else if (props.italic) {
		canvasContext.font = `${pxsize}px ${props.fontface} Italic`;
	} else if (props.bold) {
		canvasContext.font = `${pxsize}px ${props.fontface} Bold`;
	} else {
		canvasContext.font = `${pxsize}px ${props.fontface}`;
	}

	/**
	 * Process each original line
	 */
	lines.forEach((line) => {
		if (line.length > 0) {
			const words = line.split(' ');
			const brokenWords: string[] = [];
			const brokenWordsWithHyphen: string[] = [];

			/**
			 * Break long words and populate arrays
			 */
			words.forEach((word) => {
				if (canvasContext.measureText(word).width > props.maxPxLength) {
					const parts = breakWord(
						word,
						canvasContext,
						props,
					);
					parts.forEach((part, index) => {
						brokenWords.push(part);
						brokenWordsWithHyphen.push(index < parts.length - 1 ? `${part}-` : part);
					});
				} else {
					brokenWords.push(word);
					brokenWordsWithHyphen.push(word);
				}
			});

			/**
			 * Simulate line breaking
			 */
			let currentLine = '';
			let currentLineWithHyphen = '';
			brokenWords.forEach((word, index) => {
				const testLine = (
					currentLine
						? `${currentLine} ${word}`
						: word
				);

				if (
					canvasContext.measureText(testLine).width > props.maxPxLength
					&& currentLine !== ''
				) {
					arrLines.push(currentLine);
					arrLinesWithHyphen.push(currentLineWithHyphen);
					currentLine = word;
					currentLineWithHyphen = brokenWordsWithHyphen[index];
				} else {
					currentLine = testLine;
					currentLineWithHyphen = (
						currentLineWithHyphen
							? `${currentLineWithHyphen} ${brokenWordsWithHyphen[index]}`
							: brokenWordsWithHyphen[index]
					);
				}
			});

			if (currentLine !== '') {
				arrLines.push(currentLine);
				arrLinesWithHyphen.push(currentLineWithHyphen);
			}
		} else {
			arrLines.push(' ');
			arrLinesWithHyphen.push(' ');
		}
	});

	/**
	 * Calculate width and height of text div
	 */
	const textheight = arrLines.length * 1.25 * pxsize;

	return {
		pointsize,
		valid: textheight < props.maxPxHeight,
		lines: arrLines,
		linesWithHyphen: arrLinesWithHyphen,
	};
}

/**
 * Breaks and formats text to fit within specified dimensions, adjusting font size via binary search.
 * Uses canvas-based measurements for performance, suitable for real-time resizing in an editor.
 * @param props The `breakModel` properties defining the text, constraints, and styling
 * @returns A `BreakTextResult` object with the formatted text and optimal pointsize, or `false` if no text is provided
 * @throws Error if no suitable pointsize is found
 */
export default function breakText(props: breakModel): BreakTextResult | false {
	/**
	 * Note: fixed bug where we need to convert number in text
	 */
	const phrase = filterText(
		String(props.phrase),
		props.subset,
	);

	/**
	 * Split the text string by hard line breaks
	 */
	const lines = phrase.split('\n');

	/**
	 * Create an array where we store the result of all tested pointsizes
	 */
	const tested: testModel[] = [];

	/**
	 * If there is no text, stop and return false value
	 */
	if (phrase.length === 0) {
		return false;
	}

	/**
	 * Create canvas for text measurement
	 */
	const canvas = document.createElement('canvas');
	const canvasContext = canvas.getContext('2d')!;
	/**
	 * No need to set size for measurement
	 */
	canvas.width = 0;
	canvas.height = 0;

	if (
		props.resize.up
		|| props.resize.down
	) {
		/**
		 * Perform binary search for optimal pointsize, O(log n) more efficient than O(n)
		 */
		/**
		 * Minimum pointsize defaults to 1
		 */
		let low = (
			props.resize.down
			|| 1
		);
		/**
		 * Upper limit
		 */
		let high = (
			props.resize.up
			|| props.pointsize
		);
		let mid = props.pointsize;

		while (low <= high) {
			const testResult = testPointsize(
				mid,
				canvasContext,
				lines,
				props,
			);
			tested.push(testResult);
			const textheight = testResult.lines.length * 1.25 * (mid * 31496 / 15120);
			/**
			 * Width of first line
			 */
			const textwidth = canvasContext.measureText(testResult.lines[0]).width;

			if (
				textheight > props.maxPxHeight
				|| (
					testResult.lines.length === 1
					&& textwidth > props.maxPxLength
				)
			) {
				/**
				 * Too large, reduce pointsize
				 */
				high = mid - 1;
			} else if (
				textwidth < 0.9 * props.maxPxLength
				|| textheight < 0.9 * props.maxPxHeight
			) {
				/**
				 * Too small, increase pointsize
				 */
				low = mid + 1;
			} else {
				/**
				 * Optimal size found
				 */
				break;
			}

			mid = Math.floor((low + high) / 2);
		}
	} else {
		tested.push(
			testPointsize(
				props.pointsize,
				canvasContext,
				lines,
				props,
			),
		);
	}

	/**
	 * Select the best-fitting pointsize
	 */
	const bestFit = getBestFit(tested);

	if (!bestFit) {
		throw new Error('Could not find a best fit');
	}

	const pxsize = bestFit.pointsize * 31496 / 15120;
	let textFormatted = bestFit.lines.join('\n');
	let textFormattedForCanvas = bestFit.linesWithHyphen.join('\n');
	let finalLinesCount = bestFit.lines.length;

	/**
	 * Handle text truncation if at minimum size and overflowing
	 */
	if (
		props.resize.down
		&& bestFit.pointsize === props.resize.down
	) {
		const lineheight = pxsize * 1.25;

		/**
		 * Check if the text overflows the box, if it does, truncate
		 * the text, add ellipsis at the end and set the final lines count
		 * to the number of lines that fit in the box
		 */
		if (bestFit.lines.length * lineheight > props.maxPxHeight) {
			const lineToBreak = Math.floor(props.maxPxHeight / lineheight);
			textFormatted = `${bestFit.lines
				.slice(
					0,
					lineToBreak,
				)
				.join('\n')
				.slice(
					0,
					-3,
				)}...`;
			textFormattedForCanvas = `${bestFit.linesWithHyphen
				.slice(
					0,
					lineToBreak,
				)
				.join('\n')
				.slice(
					0,
					-3,
				)}...`;
			finalLinesCount = lineToBreak;
		}
	}

	/**
	 * Calculate final text height
	 */
	const textheight = finalLinesCount * 1.25 * pxsize;

	return {
		pointsize: bestFit.pointsize,
		phraseArray: bestFit.lines,
		textheight,
		text_formatted: textFormatted,
		text_formatted_for_canvas: textFormattedForCanvas,
	};
}
